import { A, useParams } from '@solidjs/router';
import { createEffect, createMemo, type JSX, on, Show } from 'solid-js';
import { PageHeader } from '../components/PageHeader';
import { RunExportButton } from '../components/RunExportButton';
import { RunOutcomeBanner } from '../components/RunOutcomeBanner';
import { RunStageProgress } from '../components/RunStageProgress';
import { RunStopAction } from '../components/RunStopAction';
import { RunTimelinePanel } from '../components/RunTimelinePanel';
import { StatusPill, type StatusTone } from '../components/StatusPill';
import { StopTaskModal } from '../components/StopTaskModal';
import { Toast } from '../components/Toast';
import { buttonClass } from '../components/ui/Button';
import { t } from '../i18n';
import { latestRunStage, useFleetRunStream } from '../lib/fleetStream';
import { orgHref } from '../lib/org';
import { SESSION_TOKEN } from '../lib/session';
import type { StreamStatus } from '../lib/sseFrames';
import { createTaskStop } from '../lib/taskStop';
import { useTaskDetail } from '../lib/tasks';
import { createToast } from '../lib/toast';

// Fleet-run timeline view (#59) — a live SSE feed of one BYOVM run's
// `task_run_events` over the NATS→SSE bridge (#58). Mirrors <AuditSession/>.
//
// A FINISHED RUN DOES NOT LAND STRAIGHT ON `done`, and this comment used to say
// it did. `run_finished` is emitted by only 2,854 of 7,139 prod runs, so for the
// other 60% the route's terminal event never arrives; what ends those streams is
// the API's idle proof (`runIsIdle` in `@developerz/domain`, applied by
// `apps/api/src/routes/fleet-events.ts` — the newest event older than half an
// hour), and until that landed this tab
// re-connected on every recycle, for ever, on runs that ended weeks ago.
//
// The pill says what the STREAM is doing. What the RUN did is a different
// question with a different answer — see <RunOutcomeBanner/>, which reads it off
// the run's own `error` events because the terminal ping carries no outcome at
// all. Both are on the page because "ended" and "worked" are not the same fact.
const STATUS_LABEL: Record<StreamStatus, string> = {
  connecting: 'fleet.status_connecting',
  live: 'fleet.status_live',
  done: 'fleet.status_done',
  error: 'fleet.status_disconnected',
};

const STATUS_TONE: Record<StreamStatus, StatusTone> = {
  connecting: 'neutral',
  live: 'live',
  done: 'success',
  error: 'danger',
};

// `token` is injectable for tests (mirrors Fleet / AuditSession); the router
// mounts this without props → SESSION_TOKEN (dev session or null).
export function FleetAudit(props: { token?: string | null } = {}): JSX.Element {
  const token = props.token !== undefined ? props.token : SESSION_TOKEN;
  const params = useParams<{ run_id: string }>();
  const { events, status, refusal, retry } = useFleetRunStream(() => params.run_id, token);
  // The run's own task, taken from the stream this view already consumes: every
  // projected event carries `task_id`, so no second address and no API change are
  // needed to know what a stop here would act on. Null until the first frame
  // lands — the control simply is not there yet, which is honest.
  const taskId = createMemo(() => events()[0]?.task_id ?? null);
  // The stream carries no status, title or repo, and the confirm needs all three
  // (and the terminal rule needs the status). One point read answers all of it —
  // a LIVE one (`lib/live/live-query.ts`): a `tasks` patch for this id re-reads it.
  const task = useTaskDetail(taskId, token);
  // …and so does the run log reaching its terminal row. That is the run's own
  // news that its task moved, and it reaches a task the `tasks` topic never
  // carries (older than its snapshot head) or a view with no shared stream. A
  // re-READ, never a status inferred from the log: the Stop control's terminal
  // rule is the task row's, and the row is what changes.
  createEffect(
    on(status, (now, before) => {
      if (now === 'done' && before !== 'done' && taskId() !== null) void task.refetch();
    }),
  );
  const { toast, notify, dismiss } = createToast();
  const stop = createTaskStop({
    token,
    onStopped: ({ alreadyTerminal }) => {
      // Re-READ: the run stream tails `task_run_events` and a cancel writes the
      // TASK row, so nothing on this view would otherwise repaint the control away.
      void task.refetch();
      notify(
        alreadyTerminal ? t('fleet.stop.already_terminal_toast') : t('fleet.stop.stopped_toast'),
      );
    },
  });

  return (
    <>
      <PageHeader
        title={t('fleet.audit_title')}
        eyebrow={t('app.live')}
        desc={t('fleet.audit_subtitle')}
        actions={
          <>
            <StatusPill tone={STATUS_TONE[status()]}>{t(STATUS_LABEL[status()])}</StatusPill>
            <RunExportButton runId={params.run_id} events={events()} />
            <RunStopAction task={task.data ?? null} onStop={stop.ask} />
            {/* What this run LEFT BEHIND (arc 101 slice 06), beside what it did.
                Gated on the resolved task id for the same reason the stop control
                above is: the id comes off the first frame, so before one lands
                there is no address to send anyone to — the control is simply not
                there yet, and appears the moment the stream resolves it. A link
                rendered early would navigate to `/fleet/tasks//report`, which is
                the one outcome worse than no link.

                It does NOT gate on whether a report exists, and it must not:
                `GET /v1/tasks/:id/report` answers 200 with `report: null` for a
                task that has delivered nothing yet, and the report view says so
                in its own words. Predicting that here would mean a second read on
                every run view to hide a link that already tells the truth. */}
            <Show when={taskId()}>
              {(id) => (
                <A
                  class={buttonClass({ variant: 'outline' })}
                  href={orgHref(`/fleet/tasks/${id()}/report`)}
                >
                  {t('fleet.view_report')}
                </A>
              )}
            </Show>
            {/* Deep route has no nav entry (#298) — keep an explicit way back. */}
            <A class={buttonClass({ variant: 'outline' })} href={orgHref('/fleet')}>
              {t('fleet.back')}
            </A>
          </>
        }
      />

      <RunOutcomeBanner events={events()} />
      <RunStageProgress stage={latestRunStage(events())} settled={events().length > 0} />
      <RunTimelinePanel
        runId={params.run_id}
        events={events()}
        status={status()}
        refusal={refusal()}
        onRetry={retry}
        token={token}
      />

      <StopTaskModal task={stop.target()} onConfirm={stop.confirm} onClose={stop.close} />
      <Toast message={stop.error()} tone="error" onDismiss={stop.clearError} />
      <Toast
        message={toast()?.message ?? null}
        tone={toast()?.tone ?? 'success'}
        onDismiss={dismiss}
      />
    </>
  );
}
